import { CloseSharpIcon } from '@assets/icons';
import {
  Autocomplete,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TagInputField.scss';
import { Regex } from '@utils/constants';

const getOptionLabel = (option: string | Option) => {
  return typeof option === 'string' ? option : option.label;
};

const TagInputField: FC<AppAutoCompleteProps> = ({
  id,
  required,
  label,
  isRow = false,
  limitTags,
  options = [],
  placeholder,
  valueType = 'string',
  value,
  defaultValue,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [values, setValues] = useState<string[]>([]);

  const errorText = useMemo(() => {
    if (error?.type === 'required') {
      return t('message.MSE00002', { 0: t(label!) });
    }
    return error?.message ? t(error?.message) : null;
  }, [error]);

  useEffect(() => {
    if (defaultValue) {
      setValues(
        typeof defaultValue === 'string'
          ? defaultValue.split(',')
          : defaultValue,
      );
    }
  }, []);

  useEffect(() => {
    if (value) {
      setValues(typeof value === 'string' ? value.split(',') : value);
    }
  }, [value]);

  const addTag = (newValue: string) => {
    if (newValue) {
      const oldValueIdx = values.findIndex((tag) => tag === newValue);
      if (oldValueIdx > -1) {
        values.splice(oldValueIdx, 1);
      }

      const newValues = values.concat(newValue);
      setValues(newValues);
      onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
    }

    setInputValue('');
  };

  const handleChange = (
    _event: React.SyntheticEvent | null,
    newValue: (string | Option)[],
  ) => {
    const newValues = newValue.map((value) => getOptionLabel(value));
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ' ', ','].includes(event.key)) {
      const target = event.target as HTMLInputElement;
      addTag(target.value);
      event.preventDefault();
    } else if (Regex.RULE.SPECIAL.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleDelete = (option: string | Option) => {
    const deleteValue = getOptionLabel(option);
    const newValues = values.filter((ele) => ele !== deleteValue);
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleBlur = () => {
    addTag(inputValue);
  };

  return (
    <Grid
      container
      className="TagInputField"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid item xs={4}>
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} sx={{ width: '100%', height: 'max-content' }}>
        <Autocomplete
          id={id}
          className={'TagInput' + (limitTags ? ' TagInput--limit' : '')}
          multiple
          freeSolo
          size="small"
          limitTags={limitTags}
          getOptionLabel={getOptionLabel}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={getOptionLabel(option)}
                  {...tagProps}
                  color="primary"
                  deleteIcon={<CloseSharpIcon fontSize="small" />}
                  onDelete={() => handleDelete(option)}
                />
              );
            })
          }
          inputValue={inputValue}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={!value ? t(placeholder!) : ''}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
            />
          )}
          options={options}
          value={values}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default TagInputField;
